/**
 * Help Center endpoints — in-app guides + bug/feature feedback.
 *
 * Talks to apps/api routes/help.ts. Reads are available to any signed-in user;
 * article authoring + feedback triage/config require an admin token (enforced
 * server-side). Responses are the bare `{ ok, ... }` shape (no `data` wrapper),
 * so the client returns the whole object — read the named field off it.
 */

import { apiGet, apiPost, apiPut, apiDelete, buildQuery } from '../client.js';

/** @returns {Promise<{ ok: boolean, sections: any[] }>} */
export const getHelpSections = () => apiGet('/api/help/sections');

/** @param {{ section?: string, all?: boolean }} [opts] */
export const getHelpArticles = (opts = {}) =>
  apiGet(`/api/help/articles${buildQuery({ section: opts.section, all: opts.all ? 'true' : undefined })}`);

/** @param {string} id */
export const getHelpArticle = (id) => apiGet(`/api/help/articles/${encodeURIComponent(id)}`);

/** @param {string} q @param {string} [section] */
export const searchHelp = (q, section) => apiGet(`/api/help/search${buildQuery({ q, section })}`);

/**
 * "Can Omni do this?" — a plain-English question answered from the guides.
 * @param {string} query
 * @returns {Promise<{ ok: boolean, answer: any }>}
 */
export const assistHelp = (query) => apiPost('/api/help/assist', { body: { query } });

/** Admin: create/update an article (no id = create). */
export const saveHelpArticle = (article) =>
  article.id
    ? apiPut(`/api/help/articles/${encodeURIComponent(article.id)}`, { body: article })
    : apiPost('/api/help/articles', { body: article });

/** Admin: delete an article. */
export const deleteHelpArticle = (id) => apiDelete(`/api/help/articles/${encodeURIComponent(id)}`);

/**
 * Restore a built-in guide to the text shipped with this build. The way back
 * for a guide that was edited here and has therefore stopped picking up
 * improvements from deploys.
 */
export const resetHelpArticle = (id) => apiPost(`/api/help/articles/${encodeURIComponent(id)}/reset`, { body: {} });

/** Which built-in guides have drifted from the shipped text, and why (admin). */
export const getHelpBuiltInStatus = () => apiGet('/api/help/built-in-status');

/** Submit a bug report / feature request / improvement. */
export const submitHelpFeedback = (feedback) => apiPost('/api/help/feedback', { body: feedback });

/** Admin: list submitted feedback. */
export const getHelpFeedback = () => apiGet('/api/help/feedback');

/** Admin: get the effective feedback config (recipients + GitHub). */
export const getHelpFeedbackConfig = () => apiGet('/api/help/feedback-config');

/** Admin: save the feedback config. */
export const saveHelpFeedbackConfig = (config) => apiPost('/api/help/feedback-config', { body: config });

/** Admin: test the configured GitHub token/repo (no issue created). */
export const testHelpGithub = () => apiPost('/api/help/feedback-config/test-github');
